import {
  DineInRound,
  DineInRoundItem,
} from "../../../../../../interfaces/DineInObject";
import VariantLineKeyHelper from "../../../../../../services/helpers/VariantLineKeyHelper";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import {
  DINE_IN_ITEM_STATUS,
  normalizeDineInItemStatus,
  buildProductIndex,
  lineIdentityKey,
  resolveDineInLine,
  type DineInProductIndex,
} from "../../../../../../services/helpers/DineInSummaryHelper";

// ─── Dine In board — one table's ticket ───────────────────────────────────
// Turns a session's rounds and items into the kitchen's view of them. Pure:
// no React, no store. Separate from dineInFeedBuilder (the diner's feed) on
// purpose — the two group and order the same data differently because they
// answer different questions, and merging them would mean a flag that makes
// both harder to read.

/** One line on the ticket: a dish, a quantity, and where it is in the kitchen. */
export interface DineInKitchenItem {
  /** Unique within its round — grouping key plus round id. */
  key: string;
  roundId: string;
  productId: string;
  variantId?: string;
  /** Summed across every raw entry merged into this line. */
  quantity: number;
  /** Normalised status shared by every entry in the line. */
  status: string;
  /** Earliest addedAt in the group, for stable ordering. */
  addedAt: string;
  /** unit price × quantity, or null while the product is still resolving. */
  lineTotal: number | null;
  /**
   * SERVER ids of every raw entry behind this line — what a decision call
   * acts on, one request per id.
   *
   * Can be shorter than the group, or empty: an entry whose response predates
   * the itemId field has no server identity, and a client-side composite can't
   * address a row. Callers must treat an empty list as "not actionable" rather
   * than assuming one id per unit of quantity.
   */
  sourceItemIds: string[];
}

/**
 * What the round's header offers on its right-hand side.
 *  - needsAction:     items are waiting on accept/reject
 *  - deliverable:     everything decided, some accepted items still to go out
 *  - done:            every item is positively known to be served or rejected
 *  - unknown:         the statuses don't map to this build's vocabulary
 *
 * `unknown` exists because the alternative was lying. It used to default to
 * `done` whenever nothing was pending or deliverable — which meant a round
 * whose items came back with an unrecognised status (the live API currently
 * sends "DRAFT" on items, not ORDERED) rendered "ALL DELIVERED" over food
 * nobody had touched. A header that says nothing is recoverable; a header that
 * confidently says the wrong thing gets a table forgotten.
 */
export type DineInRoundActionState =
  | "needsAction"
  | "deliverable"
  | "done"
  | "unknown";

export interface DineInKitchenRound {
  roundId: string;
  roundNumber: number;
  /** When the table placed this round. */
  orderedAt: string;
  items: DineInKitchenItem[];
  actionState: DineInRoundActionState;
  /** Items still awaiting accept/reject — the "N NEED ACTION" count. */
  pendingCount: number;
  /** Server ids of accepted-but-not-yet-served items — "mark all served". */
  deliverableItemIds: string[];
}

/**
 * Merges raw entries into ticket lines.
 *
 * Grouped by product and status ONLY — deliberately NOT by who ordered it,
 * which is the one difference from the diner's grouping. A cook making four
 * butter naans does not care that two were Riya's and two were Pavan's; they
 * care that there are four and that none have gone out. Splitting the line by
 * person would turn one ticket entry into several and invite half a dish being
 * marked delivered.
 *
 * Status still splits lines, because two naans the kitchen has accepted and
 * two it hasn't are genuinely different work.
 */
export function groupKitchenItems(
  items: DineInRoundItem[],
  products: DineInProductIndex
): DineInKitchenItem[] {
  const groups = new Map<string, DineInKitchenItem>();

  for (const item of items) {
    const status = normalizeDineInItemStatus(item.status);
    const key = lineIdentityKey(item, item.roundId, status);
    const existing = groups.get(key);

    if (existing) {
      existing.quantity += item.quantity && item.quantity > 0 ? item.quantity : 1;
      if (item.roundItemId) existing.sourceItemIds.push(item.roundItemId);
      if (item.addedAt && (!existing.addedAt || item.addedAt < existing.addedAt)) {
        existing.addedAt = item.addedAt;
      }
      continue;
    }

    groups.set(key, {
      key,
      roundId: item.roundId,
      productId: item.productId,
      variantId: item.variantId,
      quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
      status,
      addedAt: item.addedAt,
      lineTotal: null, // filled below, once the group's final quantity is known
      sourceItemIds: item.roundItemId ? [item.roundItemId] : [],
    });
  }

  // Priced after grouping, not during: quantity is only final once every raw
  // entry has been folded in, and pricing per-entry then summing would round
  // differently from pricing the finished line.
  return Array.from(groups.values())
    .map((group) => {
      const { unitPrice } = resolveDineInLine(group, products);
      return {
        ...group,
        lineTotal: unitPrice != null ? unitPrice * group.quantity : null,
      };
    })
    .sort((a, b) => a.addedAt.localeCompare(b.addedAt));
}

/**
 * Builds the ticket: one entry per round, OLDEST FIRST.
 *
 * This was reverse-chronological at first, on the theory that a kitchen wants
 * the newest round without scrolling. In practice it read as confusing: rounds
 * counted downward while the items inside each one counted upward, so the eye
 * had to change direction at every round boundary to follow what the table
 * actually did.
 *
 * Chronological throughout, with the view pinned to the bottom on open (see
 * useStickToBottom), gets both: one consistent direction to read in, and the
 * newest round already on screen. Same shape as any message thread, which is
 * what a table's order history effectively is.
 *
 * Only rounds the table has actually placed reach the kitchen (draft rounds
 * are visible to session members only), so there's no open-round filtering
 * here. The guard against a DRAFT round slipping through lives in the caller,
 * where it can be stated once rather than assumed in every derivation.
 */
export function buildKitchenRounds(
  rounds: DineInRound[],
  roundItems: DineInRoundItem[],
  products: ProductDetailsModal[]
): DineInKitchenRound[] {
  const productIndex = buildProductIndex(products);
  return [...rounds]
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .map((round) => {
      const items = groupKitchenItems(
        roundItems.filter((item) => item.roundId === round.roundId),
        productIndex
      );

      const pendingCount = items.filter(
        (item) => item.status === DINE_IN_ITEM_STATUS.ORDERED
      ).length;

      const deliverableItemIds = items
        .filter((item) => item.status === DINE_IN_ITEM_STATUS.ACCEPTED)
        .flatMap((item) => item.sourceItemIds);

      // "done" has to be EARNED — every item positively identified as served
      // or rejected — rather than assumed from the absence of work. Anything
      // else falls through to "unknown", which renders no claim at all.
      const settledCount = items.filter(
        (item) =>
          item.status === DINE_IN_ITEM_STATUS.SERVED ||
          item.status === DINE_IN_ITEM_STATUS.REJECTED
      ).length;

      let actionState: DineInRoundActionState = "unknown";
      if (pendingCount > 0) actionState = "needsAction";
      else if (deliverableItemIds.length > 0) actionState = "deliverable";
      else if (items.length > 0 && settledCount === items.length) actionState = "done";

      return {
        roundId: round.roundId,
        roundNumber: round.roundNumber,
        orderedAt: round.orderedAt,
        items,
        actionState,
        pendingCount,
        deliverableItemIds,
      };
    });
}

/** One line on the bill: a dish, how many of it, what it costs in total. */
export interface DineInBillLine {
  productId: string;
  variantId?: string;
  /** "Biryani · Full", or "" while the product is still resolving. */
  title: string;
  quantity: number;
  /** unit price × quantity, or null if the product hasn't resolved. */
  lineTotal: number | null;
}

/**
 * Collapses a whole session into a bill.
 *
 * Grouped by product ACROSS rounds, which is the third grouping in this
 * feature and deliberately so — a diner's feed groups by who ordered, a
 * kitchen ticket groups by round and status because those are units of work,
 * but a bill is neither. Nobody wants to pay for "2 naan in round 1 and 2 naan
 * in round 3"; they want a line saying 4 naan.
 *
 * Rejected items are dropped rather than struck through, and counted
 * separately: a bill is a demand for money, and a line the table isn't being
 * charged for has no business appearing on it. The count is returned so the UI
 * can still say something happened, rather than leaving staff wondering why a
 * dish they remember is missing.
 *
 * Items whose product hasn't resolved keep their line (the table did order
 * something) but contribute nothing to the total, same convention as every
 * other total in this feature — a bill that silently invents ₹0 for a real
 * dish is worse than one that visibly can't price it yet.
 */
export function buildBillLines(
  roundItems: DineInRoundItem[],
  products: ProductDetailsModal[]
): { lines: DineInBillLine[]; total: number; rejectedCount: number } {
  const productIndex = buildProductIndex(products);
  const byProduct = new Map<string, DineInBillLine>();
  let rejectedCount = 0;

  for (const item of roundItems) {
    if (normalizeDineInItemStatus(item.status) === DINE_IN_ITEM_STATUS.REJECTED) {
      rejectedCount += item.quantity && item.quantity > 0 ? item.quantity : 1;
      continue;
    }
    const lineKey = VariantLineKeyHelper.toLineKey(item.productId, item.variantId);
    const existing = byProduct.get(lineKey);
    if (existing) existing.quantity += item.quantity && item.quantity > 0 ? item.quantity : 1;
    else
      byProduct.set(lineKey, {
        productId: item.productId,
        variantId: item.variantId,
        title: "",
        quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
        lineTotal: null,
      });
  }

  let total = 0;
  const lines = Array.from(byProduct.values()).map((line) => {
    const { unitPrice, displayTitle } = resolveDineInLine(line, productIndex);
    const lineTotal = unitPrice != null ? unitPrice * line.quantity : null;
    if (lineTotal != null) total += lineTotal;
    return { ...line, title: displayTitle, lineTotal };
  });

  return { lines, total, rejectedCount };
}

/** Total distinct rounds and plates on the ticket, for the summary strip. */
export function summariseKitchenRounds(rounds: DineInKitchenRound[]): {
  roundCount: number;
  itemCount: number;
} {
  let itemCount = 0;
  for (const round of rounds) {
    for (const item of round.items) {
      // Rejected dishes aren't work and aren't money — same exclusion the
      // board tiles and the bill total apply.
      if (item.status === DINE_IN_ITEM_STATUS.REJECTED) continue;
      itemCount += item.quantity;
    }
  }
  return { roundCount: rounds.length, itemCount };
}
